import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import useScrollToTop from '../../hooks/useScrollToTop';
import { TeamMemberData, ValueItem, StatItem } from '../../types/components';

const About: FC = () => {
  // Scroll to top when component mounts
  useScrollToTop();

  const teamMembers: TeamMemberData[] = [
    {
      name: 'Rishabh',
      role: 'CEO & Founder',
      bio: 'Visionary leader passionate about connecting talent with opportunity and revolutionizing job discovery.',
      image: 'RJ',
      linkedin: '#',
      twitter: '#'
    },
    {
      name: 'Niraj',
      role: 'CTO',
      bio: 'Technology expert building the future of job matching with cutting-edge AI and machine learning solutions.',
      image: 'N',
      linkedin: '#',
      twitter: '#'
    },
    {
      name: 'Vasu',
      role: 'Head of Product',
      bio: 'Product strategist focused on creating intuitive user experiences that make job discovery seamless.',
      image: 'VR',
      linkedin: '#',
      twitter: '#'
    },
    {
      name: 'Krishnaraj',
      role: 'Head of Engineering',
      bio: 'Full-stack architect with expertise in scalable systems, leading our technical infrastructure development.',
      image: 'K',
      linkedin: '#',
      twitter: '#'
    },
    {
      name: 'Sameer',
      role: 'Head of Operations',
      bio: 'Operations specialist ensuring smooth operations across all touchpoints and driving operational excellence.',
      image: 'S',
      linkedin: '#',
      twitter: '#'
    }
  ];

  const values: ValueItem[] = [
    {
      title: 'User Centric',
      description: `Every decision we make is guided by what's best for our users and their success`,
      icon: <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
    },
    {
      title: 'Smart + Human',
      description: 'We blend AI intelligence with human insight to ensure every match feels right.',
      icon: <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
    },
    {
      title: 'Verified & Trusted',
      description: 'Authenticity matters — every profile and job on Dhoondh is verified for safety and trust.',
      icon: <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    },
    {
      title: 'Growth For All',
      description: 'We go beyond placements — helping people grow careers and companies build stronger teams.',
      icon: <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
    }
  ];

  const stats: StatItem[] = [
    { number: 'Growing', label: 'Active Community', description: 'Job seekers and employers' },
    { number: 'Quality', label: 'Job Postings', description: 'Curated opportunities' },
    { number: 'Trusted', label: 'Companies', description: 'Verified employers' },
    { number: 'High', label: 'Success Rate', description: 'Successful matches' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-primary-teaGreen/5 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 md:pt-32 pb-20 md:pb-32 bg-gradient-to-br from-primary-teaGreen/5 via-white to-primary-lightGreen/10">
        {/* Enhanced Background decorations */}
        <div className="absolute -top-40 -right-40 h-80 w-80 md:h-96 md:w-96 rounded-full bg-primary-teaGreen/20 blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 h-80 w-80 md:h-96 md:w-96 rounded-full bg-primary-lightGreen/15 blur-3xl animate-pulse" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-primary-teaGreen/5 blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
        
        {/* Additional floating elements */}
        <div className="absolute top-32 right-1/4 w-32 h-32 rounded-full bg-primary-lightGreen/10 blur-2xl animate-pulse" style={{animationDelay: '2s'}} />
        <div className="absolute bottom-32 left-1/4 w-24 h-24 rounded-full bg-primary-teaGreen/15 blur-2xl animate-pulse" style={{animationDelay: '3s'}} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex items-center px-6 py-3 rounded-full text-base font-semibold bg-primary-teaGreen/30 text-primary-black mb-8 border border-primary-teaGreen/40 shadow-lg">
              About Dhoondh
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-primary-black mb-8 leading-tight">
              Building the Future of
              <br className="hidden sm:block mb-3" />
              <span style={{lineHeight: '1.2'}} className="text-primary-lightGreen tracking-tighter">Job Market Place</span>
            </h1>
            
            {/* Enhanced value propositions */}
            <div className="max-w-5xl mx-auto mb-12">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 shadow-xl border border-gray-100 mb-6">
                <p className="text-xl md:text-2xl lg:text-3xl text-gray-700 mb-4 leading-relaxed">
                  <strong className="text-primary-black">We offer hiring services to companies</strong> <strong className="text-primary-lightGreen">completely free of charge</strong><br />
                  <span className="text-lg md:text-xl">unlike most hiring services, we do not levy any fees for candidate hirings.</span>
                </p>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 shadow-xl border border-gray-100">
                <p className="text-xl md:text-2xl lg:text-3xl text-gray-700 leading-relaxed">
                  <strong className="text-primary-black">For job seekers,</strong> <strong className="text-primary-lightGreen">we offer a 6-month placement guarantee:</strong><br />
                  <span className="text-lg md:text-xl">If we are unable to place a candidate in 6 months, we commit to <strong className="text-primary-lightGreen">full refund</strong>.</span>
                </p>
              </div>
            </div>

            {/* Enhanced CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/jobs">
                <Button size="lg" className="px-10 py-5 text-xl font-semibold shadow-lg shadow-primary-lightGreen/30 hover:shadow-primary-lightGreen/50 transition-all duration-300">
                  Explore Jobs
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg" className="px-10 py-5 text-xl font-semibold border-2 border-primary-lightGreen text-primary-black hover:bg-primary-teaGreen/20 transition-all duration-300">
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => {
              const icons = [
                // Growing - Active Community (Job seekers and employers)
                <svg className="w-12 h-12 text-primary-lightGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>,
                // Quality - Job Postings (Curated opportunities)
                <svg className="w-12 h-12 text-primary-lightGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>,
                // Trusted - Companies (Verified employers)
                <svg className="w-12 h-12 text-primary-lightGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>,
                // High - Success Rate (Successful matches)
                <svg className="w-12 h-12 text-primary-lightGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ];

              return (
                <div key={index} className="text-center group">
                  <div className="flex justify-center mb-4">
                    <div className="p-4 bg-primary-lightGreen/10 rounded-full group-hover:bg-primary-lightGreen/20 transition-colors duration-300">
                      {icons[index]}
                    </div>
                  </div>
                  <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-black mb-2 group-hover:text-primary-teaGreen transition-colors duration-300">
                    {stat.number}
                  </div>
                  <div className="text-sm md:text-base font-semibold text-gray-700 mb-1">
                    {stat.label}
                  </div>
                  <div className="text-xs md:text-sm text-gray-500">
                    {stat.description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary-black mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At Dhoondh, we're building a next-generation job marketplace that transforms how people find meaningful careers and how companies connect with top talent.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our mission is simple: make job discovery affordable, hiring effortless, and growth accessible to everyone.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="md" className="px-6 py-3">
                    Join Our Community
                  </Button>
                </Link>
                <Link to="/jobs">
                  <Button variant="outline" size="md" className="px-6 py-3">
                    Browse Opportunities
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-primary-teaGreen/20 to-primary-lightGreen/20 rounded-3xl p-8 md:p-12">
                <div className="text-center">
                  <div className="text-6xl md:text-7xl font-bold text-primary-black mb-4">2025</div>
                  <div className="text-xl text-gray-600 mb-6">Founded</div>
                  <div className="text-lg text-gray-700">
                  <i>"Empowering India to find work that works."</i>
                  </div>
                </div>
              </div>
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-primary-lightGreen/30 rounded-full animate-pulse"></div>
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-primary-teaGreen/40 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 md:py-24 bg-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative">
              <div className="bg-gradient-to-br from-primary-teaGreen/20 to-primary-lightGreen/20 rounded-3xl p-8 md:p-12">
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-primary-black mb-4">
                    Our Values
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {values.map((value, index) => (
                    <div key={index} className="group text-center bg-white/60 rounded-xl p-4 hover:bg-white/80 transition-all duration-300">
                      <div className="w-12 h-12 bg-primary-teaGreen/30 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-teaGreen/40 transition-colors duration-300">
                        <div className="text-primary-black">
                          {value.icon}
                        </div>
                      </div>
                      <h4 className="text-sm font-bold text-primary-black mb-2">
                        {value.title}
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-primary-lightGreen/30 rounded-full animate-pulse"></div>
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-primary-teaGreen/40 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary-black mb-6">
                Our Vision
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                To become India's most trusted and innovative job marketplace, where every professional finds their perfect role and every company discovers exceptional talent.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We envision a future where job searching is effortless, hiring is efficient, and career growth is accessible to everyone, regardless of background or location.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="md" className="px-6 py-3">
                    Join Our Vision
                  </Button>
                </Link>
                <Link to="/jobs">
                  <Button variant="outline" size="md" className="px-6 py-3">
                    Explore Opportunities
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-black mb-4">
              Meet Our Team
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The passionate individuals behind Dhoondh, working together to revolutionize job discovery.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="group bg-white/90 backdrop-blur border border-gray-100 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-primary-lightGreen to-primary-teaGreen rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-primary-black font-bold text-xl">
                      {member.image}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-primary-black mb-2">
                    {member.name}
                  </h3>
                  <p className="text-primary-teaGreen font-semibold mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {member.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary-teaGreen/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-r from-primary-teaGreen/30 to-primary-lightGreen/20 p-8 md:p-12 lg:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-black mb-4">
              Ready to Join Our Journey?
            </h2>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              Whether you're looking for your next opportunity or seeking exceptional talent, 
              Dhoondh is here to help you succeed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="px-8 py-4 text-lg">
                  Get Started Today
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg" className="px-8 py-4 text-lg border-primary-black text-primary-black hover:bg-primary-black hover:text-white">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
